import { useMemo } from "react";
import { useListQuery } from "./lib/query";
import { ListPanel } from "./components/ui";
import { api } from "./lib/api";
import { Vintage, type Freshness } from "./components/provenance";

/** One screen that shows the whole vault, in the order the questions get asked.
 *
 *  WHY THIS EXISTS. Every number the vault knows was on a screen somewhere, and no screen said how
 *  they fit together. "12,310,602 addresses", "73.4% parcel cover", "89.8% rooftop", "4,928,296
 *  single family" are four answers to four different questions with four different denominators,
 *  and read side by side as tiles they invite exactly the mistake this codebase keeps making: a
 *  percentage quoted against a bottom half nobody stated. A funnel cannot make that mistake --
 *  every bar is drawn against the same baseline and every gap between two bars is a named
 *  subtraction.
 *
 *  IT COMPUTES NOTHING THE ROLLUP DOES NOT ALREADY KNOW. All of it comes from one call to
 *  /geocode/vault-coverage, which reads county_daily. No aggregate here scans a vault table, and
 *  nothing on this screen needs a job to be re-run before it is true.
 *
 *  MEASURED AND LABELLED ARE NEVER THE SAME WORD. `on_a_building` is footprint_check: every pin
 *  tested against an Overture polygon. `on_the_building` is household_confidence_tier, which for
 *  any address we did not place ourselves is the county's own geocode_quality label, inherited
 *  from TxGIO -- which aggregates 48 911 authorities and publishes no placement-method field.
 *  0215 established the difference; a screen that shows one without the other is how it gets
 *  forgotten again. Where only the labelled figure exists, this screen says "label" in the words
 *  on the tile, not in a tooltip.
 */

// ── palette ───────────────────────────────────────────────────────────────────────────────────
// Validated against the portal's dark surface (#151c27) as a categorical set: lightness band,
// chroma floor, adjacent-pair CVD separation, normal-vision separation and 3:1 contrast all pass.
// The green is a step down from the portal's --accent (#3fc079, too light for a chart fill on this
// surface) so the two read as the same family without the fills glowing.
const C = {
  sf: "#31A866", mf: "#8A79D8", vacant: "#C2871F", other: "#4A8ACB",
  unknown: "#5b6779",
  // One hue, three steps, for a quantity split by CAUSE rather than by kind (validated --ordinal).
  gap1: "#D2A24A", gap2: "#A8760F",
  bad: "#e26d5c", warn: "#e0a93b",
};

type Row = {
  fips: number; county: string | null;
  address_points: number; parcel_extents: number | null;
  on_the_building: number | null; on_the_lot: number | null; conflicted: number | null;
  checked: number | null; on_a_building: number | null; pct_on_a_building: number | null;
  not_on_a_building: number | null;
  credibility: string;
  sf_addresses: number | null; sf_has_building: number | null; sf_no_building: number | null;
  sf_building_unknown: number | null; sf_on_the_building: number | null;
  sf_checked: number | null; sf_on_a_building: number | null;
  mf_addresses: number | null; vacant_addresses: number | null; land_use_unknown: number | null;
  // pct_sf_on_a_roof is GONE, not renamed (0227): it divided the rooftop LABEL by sf_addresses --
  // the wrong numerator over the wrong denominator. Its replacements are the measured rate and the
  // labelled one beside it, over the pins actually checked.
  pct_sf_on_a_building: number | null; pct_sf_labelled_rooftop: number | null;
  pct_land_use_known: number | null;
  verdict: string;
};
type Totals = {
  counties: number; address_points: number; parcel_extents: number;
  on_the_building: number; checked: number; on_a_building: number; not_on_a_building: number;
  on_the_lot: number; conflicted: number; conflicted_credible: number;
  sf_addresses: number; sf_on_the_building: number; sf_has_building: number;
  sf_counties: number; land_use_unknown: number;
};
type Cov = { counties: Row[]; totals: Totals; freshness?: Freshness | null };

const n = (x: number) => x.toLocaleString();
const pct = (part: number, whole: number, dp = 1) =>
  whole > 0 ? `${((100 * part) / whole).toFixed(dp)}%` : "—";

const BORDER = "1px solid var(--border,#27303f)";

// ── pieces ────────────────────────────────────────────────────────────────────────────────────

function Section({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <div className="card" style={{ padding: 16, marginTop: 10 }}>
      <div style={{ fontWeight: 700, fontSize: 15 }}>{title}</div>
      {sub && <div className="muted" style={{ fontSize: 12, marginTop: 2, maxWidth: "80ch" }}>{sub}</div>}
      <div style={{ marginTop: 14 }}>{children}</div>
    </div>
  );
}

/** One rung of the funnel. Every bar is a share of the SAME baseline, which is the entire point:
 *  a stack of percentages each against its own denominator is four unrelated numbers wearing a
 *  chart. `cut` is the subtraction that got us from the rung above to this one, in words. */
function Rung({ label, hint, value, baseline, color, cut }: {
  label: string; hint: string; value: number; baseline: number; color: string; cut?: React.ReactNode;
}) {
  return (
    <div style={{ borderTop: BORDER, padding: "10px 0" }}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(150px,210px) minmax(0,1fr) 130px",
                    gap: 14, alignItems: "center" }}>
        <div style={{ fontSize: 13, lineHeight: 1.3 }}>
          {label}
          <div className="muted" style={{ fontSize: 11, marginTop: 1 }}>{hint}</div>
        </div>
        <div style={{ height: 20, background: "var(--surface-2,#1b2330)", borderRadius: 3, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${baseline > 0 ? (100 * value) / baseline : 0}%`,
                        background: color, borderRadius: 3, minWidth: 2 }}
               title={`${n(value)} — ${pct(value, baseline)} of all addresses`} />
        </div>
        <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{n(value)}</div>
          <div className="muted" style={{ fontSize: 11 }}>{pct(value, baseline)} of all</div>
        </div>
      </div>
      {cut && <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>{cut}</div>}
    </div>
  );
}

const Sub = ({ children }: { children: React.ReactNode }) =>
  <b style={{ color: "var(--warn,#e0a93b)", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{children}</b>;

/** A composition bar with its legend. Segments carry a 2px surface gap so adjacent fills never
 *  touch, and every segment is named in the legend with its own count -- identity is never left to
 *  colour alone. */
function Composition({ total, parts }: { total: number; parts: { k: string; v: number; c: string; note?: string }[] }) {
  return (
    <>
      <div style={{ display: "flex", height: 30, borderRadius: 4, overflow: "hidden",
                    background: "var(--surface-2,#1b2330)" }}>
        {parts.map((p, i) => (
          <div key={p.k} title={`${p.k} — ${n(p.v)} (${pct(p.v, total)})`}
               style={{ width: `${total > 0 ? (100 * p.v) / total : 0}%`, background: p.c, minWidth: p.v > 0 ? 3 : 0,
                        boxShadow: i ? "-2px 0 0 var(--surface,#151c27)" : undefined }} />
        ))}
      </div>
      <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 7 }}>
        {parts.map((p) => (
          <div key={p.k} style={{ display: "grid", gridTemplateColumns: "12px minmax(0,1fr) auto 54px",
                                  gap: 10, alignItems: "baseline", fontSize: 13 }}>
            <span style={{ width: 11, height: 11, borderRadius: 2, background: p.c, transform: "translateY(1px)" }} />
            <span>{p.k}{p.note && <span className="muted" style={{ fontSize: 11 }}> — {p.note}</span>}</span>
            <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{n(p.v)}</span>
            <span className="muted" style={{ fontVariantNumeric: "tabular-nums", textAlign: "right", fontSize: 12 }}>
              {pct(p.v, total)}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

/** A short ranked list of counties. Deliberately short: the full 254 live one tab over, and a
 *  screen that shows everything ranks nothing. */
function Outliers({ rows, value, caption, tone }: {
  rows: Row[]; value: (r: Row) => string; caption: string; tone?: string;
}) {
  if (rows.length === 0) return <div className="muted" style={{ fontSize: 13 }}>{caption}</div>;
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {rows.map((r) => (
        <div key={r.fips} style={{ display: "flex", justifyContent: "space-between", gap: 12,
                                   padding: "6px 0", borderTop: BORDER, fontSize: 13 }}>
          <span>{r.county || "—"} <span className="muted" style={{ fontSize: 11 }}>{r.fips}</span></span>
          <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: tone }}>{value(r)}</span>
        </div>
      ))}
    </div>
  );
}

// ── screen ────────────────────────────────────────────────────────────────────────────────────

export function VaultOverview({ onGo }: { onGo?: (tab: string, fips?: number) => void }) {
  const { query, reload } = useListQuery<Cov>(
    "vault-overview",
    (signal) => api<Cov>("/geocode/vault-coverage", { signal }),
  );
  return (
    <div>
      <div className="card" style={{ padding: 12, marginBottom: 10 }}>
        <div style={{ fontWeight: 700 }}>The vault, end to end</div>
        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
          Every subtraction between the addresses Texas publishes and the homes a canvasser can be
          driven to — each one named, and attributable to a county file or to a scan we ran.
        </div>
      </div>
      <ListPanel
        query={query}
        isEmpty={(d) => d.counties.length === 0}
        empty={<div className="card muted" style={{ padding: 14 }}>No counties have been loaded into the vault yet.</div>}
        onRetry={reload}
      >
        {(d) => <VaultOverviewBody data={d} onGo={onGo} />}
      </ListPanel>
    </div>
  );
}

/** Exported for portal/test/vault_overview_render_test.tsx, which renders it against the
 *  shapes 254 real counties actually take -- no land use, never checked, never rolled up --
 *  because every number here is a division and a type checker cannot see a NaN. */
export function VaultOverviewBody({ data, onGo }: { data: Cov; onGo?: (t: string, fips?: number) => void }) {
  const { counties, totals } = data;
  const T = totals.address_points;

  const m = useMemo(() => {
    const known = T - totals.land_use_unknown;
    const sfBuilt = totals.sf_has_building;
    const sfEmpty = counties.reduce((a, r) => a + (r.sf_no_building || 0), 0);
    const sfUnscanned = counties.reduce((a, r) => a + (r.sf_building_unknown || 0), 0);
    const mf = counties.reduce((a, r) => a + (r.mf_addresses || 0), 0);
    const vac = counties.reduce((a, r) => a + (r.vacant_addresses || 0), 0);
    const other = Math.max(0, known - totals.sf_addresses - mf - vac);

    // TWO KINDS OF SILENCE, AND THEY NEED DIFFERENT WORK. A county that publishes no land use at
    // all is a records gap nobody here can close; an address in a county that DOES publish, which
    // simply matched no coded parcel, is a keying problem and is ours. Folding them into one
    // "unknown" number is what made 6.4M addresses look like a single intractable pile.
    const silentCounties = counties.filter((r) => (r.sf_addresses || 0) === 0 && r.address_points > 0);
    const silent = silentCounties.reduce((a, r) => a + (r.land_use_unknown || 0), 0);
    const matchGap = totals.land_use_unknown - silent;

    const byRate = counties
      .filter((r) => r.pct_on_a_building != null && (r.checked || 0) >= 2000)
      .sort((a, b) => (a.pct_on_a_building as number) - (b.pct_on_a_building as number));
    const byMiss = [...counties].sort((a, b) => (b.not_on_a_building || 0) - (a.not_on_a_building || 0));
    const byUnknown = [...counties].sort((a, b) => (b.land_use_unknown || 0) - (a.land_use_unknown || 0));
    // WHERE THE CONFLICTED PILE ACTUALLY IS. The statewide total has been on a screen since 0212
    // with no way to reach the work: the queue it named is district-scoped over a different table
    // entirely (see the header comment on the panel below). Ranked by count, and only counties
    // whose own parcel data makes the flag worth believing -- 'low' and 'unmeasured' counties are
    // mostly telling you their parcel file is poor, which is 0165's whole finding.
    const byConflict = counties
      .filter((r) => (r.conflicted || 0) > 0 && (r.credibility === "high" || r.credibility === "good"))
      .sort((a, b) => (b.conflicted || 0) - (a.conflicted || 0));

    return { known, sfBuilt, sfEmpty, sfUnscanned, mf, vac, other,
             silent, silentCounties, matchGap, byRate, byMiss, byUnknown, byConflict };
  }, [counties, totals, T]);

  return (
    <>
      {/* EVERY FIGURE BELOW IS READ OUT OF ONE SNAPSHOT, so the date on it belongs at the top of
          the screen rather than beside one number. */}
      <Vintage f={data.freshness} />
      {/* ── the funnel ────────────────────────────────────────────────────────────────────── */}
      <Section
        title="From every address Texas publishes, down to homes"
        sub="Each bar is a share of the same 12.3-million baseline, so the widths compare directly and
             each gap is one named subtraction. Nothing here is a filter we chose.">
        <Rung label="Addresses in the vault" hint={`${totals.counties} counties`}
              value={T} baseline={T} color={C.unknown} />
        <Rung label="Land use is known" hint="a parcel matched, and it carries a state category code"
              value={m.known} baseline={T} color={C.other}
              cut={<>− <Sub>{n(totals.land_use_unknown)}</Sub> with no land use available. A records
                   gap, not a canvassing fact — split by cause below.</>} />
        <Rung label="Single family" hint="state category A, every spelling of it"
              value={totals.sf_addresses} baseline={T} color={C.sf}
              cut={<>− <Sub>{n(m.known - totals.sf_addresses)}</Sub> the county calls multifamily,
                   vacant, commercial, rural or exempt.</>} />
        <Rung label="…on a lot with a building" hint="the parcel scan found a structure on the lot"
              value={m.sfBuilt} baseline={T} color={C.sf}
              cut={<>− <Sub>{n(m.sfEmpty)}</Sub> single-family lots scanned and found <i>empty</i>:
                   platted, addressed, unbuilt. − <Sub>{n(m.sfUnscanned)}</Sub> the scan has not
                   reached, which is not the same thing and is never counted as empty.</>} />
      </Section>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(360px,1fr))", gap: 10 }}>
        {/* ── composition ─────────────────────────────────────────────────────────────────── */}
        <Section title="What the appraisal districts say these lots are"
                 sub={`${n(m.known)} addresses whose land use is known, by state category.`}>
          <Composition total={m.known} parts={[
            { k: "Single family", v: totals.sf_addresses, c: C.sf, note: "category A, mobile homes on their own lot included" },
            { k: "Multifamily", v: m.mf, c: C.mf, note: "addresses on category-B land, NOT units" },
            { k: "Vacant", v: m.vac, c: C.vacant, note: "category C" },
            { k: "Everything else", v: m.other, c: C.other, note: "commercial, rural, utilities, exempt" },
          ]} />
        </Section>

        {/* ── the unknown pile, split by who can fix it ───────────────────────────────────── */}
        <Section title="Why some addresses have no land use"
                 sub="Two different silences. Only one of them is ours.">
          <Composition total={totals.land_use_unknown} parts={[
            { k: `${m.silentCounties.length} counties publish no land use at all`, v: m.silent, c: C.gap2,
              note: "a records gap at the county" },
            { k: "No coded parcel matched the address", v: m.matchGap, c: C.gap1,
              note: "in counties that DO publish — ours to close" },
          ]} />
        </Section>
      </div>

      {/* ── pin quality, measured ───────────────────────────────────────────────────────────── */}
      <Section
        title="Where the pins actually sit"
        sub="Measured: every pin tested against the Overture building footprints. This is the witness,
             not the county's label — a rooftop label is inherited from authorities that publish no
             placement method, and reads several points higher than the measurement.">
        <Composition total={T} parts={[
          { k: "Tested and inside a building", v: totals.on_a_building, c: C.sf },
          { k: "Tested and not on a building", v: totals.not_on_a_building, c: C.vacant },
          { k: "Never tested", v: Math.max(0, T - totals.checked), c: C.unknown },
        ]} />
        <div className="muted" style={{ fontSize: 12, marginTop: 12, borderTop: BORDER, paddingTop: 12 }}>
          <b>{pct(totals.on_a_building, totals.checked)}</b> of the {n(totals.checked)} pins actually
          tested are on a building.{" "}
          <span title="household_confidence_tier folds county_rooftop, rooftop, rooftop_verified and rooftop_snapped into one bucket. TxGIO publishes no placement-method field, so none of them is evidence that the pin is on a building.">
            {n(totals.on_the_building)} carry a rooftop <i>label</i> — that is not the same claim and
            is not counted here.
          </span>{" "}
          {totals.on_the_lot > 0 && <>{n(totals.on_the_lot)} sit on the parcel centroid rather than a
          door, which on a large lot is not a doorstep.</>}
        </div>
      </Section>

      {/* ── who to look at ──────────────────────────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 10 }}>
        <Section title="Lowest measured rooftop rate"
                 sub="Counties with 2,000+ pins checked. A short list on purpose — the whole point of a
                      statewide rate is that it collapses to a handful of names.">
          <Outliers rows={m.byRate.slice(0, 6)} tone={C.bad}
                    value={(r) => `${r.pct_on_a_building}%`}
                    caption="No county has been footprint-checked yet." />
        </Section>
        <Section title="Most pins not on a building"
                 sub="The size of the problem, not a work queue: how many of these can be safely moved
                      has not been measured.">
          <Outliers rows={m.byMiss.slice(0, 6)} tone={C.warn}
                    value={(r) => n(r.not_on_a_building || 0)}
                    caption="Nothing has been checked yet." />
        </Section>
        <Section title="Biggest land-use gaps"
                 sub="Addresses we cannot yet call residential or not. Mostly counties that publish
                      nothing — see the split above before treating these as ours.">
          <Outliers rows={m.byUnknown.slice(0, 6)}
                    value={(r) => n(r.land_use_unknown || 0)}
                    caption="Every address has a land use." />
        </Section>
      </div>

      {/* ── THE ONE PILE SOMEBODY CAN ACTUALLY WORK, AND WHERE IT IS ─────────────────────────
          TWO QUEUES, TWO POPULATIONS, AND THE PORTAL POINTED AT THE WRONG ONE. Home has carried
          this total since 0212 under the words "Work them in Geocodes → Triage leftovers". That
          screen runs private.geocode_suspects(), which reads public.household -- the licensed
          VOTER FILE -- flags five unrelated problems (no_coord, outside_precinct, placeholder,
          cluster, low_quality), and refuses to run at all without a district picked first.

          These addresses are address_vault.address_point rows whose geocode_quality is 'suspect',
          which household_confidence_tier maps to 'conflicted'. address_vault.triage_queue() selects
          exactly those, runs statewide, and is already ordered by county credibility. It is the
          Address review sub-tab, one click away, and nothing has ever said so.

          So: name the counties, and land the reader IN the county's queue rather than at the top
          of a 254-entry select. */}
      <Section
        title="Conflicted pins, and the counties holding them"
        sub="Addresses whose pin did not land on the parcel bearing that address AND which nothing
             has corrected yet — placing a pin removes it from this count, so it falls as the
             placement passes run. Listed only for counties whose own parcel data makes the flag
             worth believing; where most addresses do NOT sit on their own parcel, it is mostly
             telling you the parcel file is poor.">
        {m.byConflict.length === 0
          ? <div className="muted" style={{ fontSize: 13 }}>No conflicts in any county measured
              credible enough for the flag to mean something.</div>
          : <div style={{ display: "flex", flexDirection: "column" }}>
              {m.byConflict.slice(0, 8).map((r) => (
                <div key={r.fips} style={{ display: "flex", justifyContent: "space-between", gap: 12,
                                           alignItems: "baseline", padding: "6px 0", borderTop: BORDER,
                                           fontSize: 13 }}>
                  <span>{r.county || "—"} <span className="muted" style={{ fontSize: 11 }}>{r.fips}</span></span>
                  <span className="row" style={{ gap: 10, alignItems: "baseline" }}>
                    <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: C.warn }}>
                      {n(r.conflicted || 0)}
                    </span>
                    {onGo && <button className="btn sm" onClick={() => onGo("addresses", r.fips)}>Work it →</button>}
                  </span>
                </div>
              ))}
            </div>}
        <div className="muted" style={{ fontSize: 12, marginTop: 12, borderTop: BORDER, paddingTop: 12 }}>
          <b>Not the same queue as Geocodes → Triage leftovers.</b> That one cleans the licensed
          voter file for one district at a time — different table, different problems, and it cannot
          be opened without picking a district first. These are addresses in the vault, and the
          Address review sub-tab works them statewide.
        </div>
      </Section>

      <div className="card" style={{ padding: 14, marginTop: 10 }}>
        <div className="muted" style={{ fontSize: 12 }}>
          Every figure here is read from <code>address_vault.county_daily</code>, written by the
          county rollup — nothing on this screen scans a vault table, and nothing needs a job re-run
          before it is true. The per-county detail, with the verdict saying whose gap each gap is,
          is one click away.
          {onGo && <> <button className="btn sm" style={{ marginLeft: 8 }}
                              onClick={() => onGo("coverage")}>Coverage by county →</button></>}
        </div>
      </div>
    </>
  );
}
